// Merkezi gerçek-zamanlı senkron köprüsü.
// Tüm mutasyon istekleri (create/update/add/remove/cancel/confirm/...-change-status)
// başarılı dönünce, ilgili "entity" için socket üzerinden bir "status:update" yayını yapılır.
// Sunucu (server.js) status:update'i sender hariç tüm bağlı kullanıcılara broadcast eder;
// alıcı sayfalar useEntityStatusSync(entity, ...) ile dinleyip kendini tazeler.
//
// Böylece her ayrı çağrıya elle emitStatusUpdate eklemek gerekmez: tek axios
// interceptor bütün mutasyonları otomatik yakalar.
import axios from "axios";
import { emitStatusUpdate } from "./statusBus";

// Okuma / kimlik doğrulama / etkisiz uçlar — yayın yapma.
const SKIP_EXACT = new Set([
  "get-token",
  "user-login",
  "user-register",
  "user-forgot-password",
  "user-confirm-otp",
  "send-otp",
  "set-user-notification-as-read",
  // Çoğul karşılığı da atlanmalı: veri değiştirmeyen bu okuma aksiyonu entity="user", id="*"
  // yayınlayıp tüm panellerde ağır tam-liste yenilemesi tetikliyordu.
  "set-all-user-notifications-as-read",
]);
const SKIP_PREFIX = ["get-", "chat-", "admin-overview-", "troyi-admin-", "check-"];

// Endpoint adından entity türet. Sıra önemli: özelden genele.
function entityOf(ep) {
  if (ep.includes("warehouse-room")) return "warehouse-room";
  if (ep.includes("warehouse-compan") || ep.startsWith("company-")) return "company";
  if (ep.includes("warehouse")) return "warehouse";
  if (ep.includes("operation") || ep.includes("team")) return "operation";
  if (ep.includes("offer")) return "offer";
  if (ep.includes("request")) return "request";
  if (ep.includes("contract") || ep.includes("signature") || ep.includes("agreement")) return "contract";
  if (ep.includes("additional-service")) return "additional-service";
  if (ep.includes("mailing")) return "mailing";
  if (
    ep.includes("user") ||
    ep.includes("customer") ||
    ep.includes("profile") ||
    ep.includes("avatar")
  ) return "user";
  return null;
}

// Kuplajlı domain ilişkileri: biri değişince türetilmiş kayıtlar da etkilenir.
// Örn. müşteri teklifi onaylayınca admin tarafında "sanal operasyon" oluşur →
// offer değişimi operation dinleyen sayfaları da tazelemeli.
// İlişkili entity'lerin spesifik id'si bilinmediği için liste düzeyi ("*") yayınlanır.
const RELATED = {
  // Teklif ONAYI backend'de operasyonla birlikte SÖZLEŞMELERİ de üretiyor
  // (offers::confirm → contracts::createForAllTemplates); sözleşme ekranları bu yayın
  // olmadan bayat kalıyordu.
  offer: ["operation", "request", "contract"],
  request: ["operation", "offer"],
  // Operasyon onayı (admin) backend'de o operasyona sözleşme(ler) oluşturur; bu FE çağrısı
  // "operation" entity'sinde olduğundan, sözleşme dinleyen sayfaları (Sözleşmelerim / Müşteri
  // Sözleşmeleri) da liste düzeyinde tazelemek için "contract" ekleniyor.
  operation: ["offer", "request", "contract"],
};

// İlgili entity için yanıtta/istekte aranacak id anahtarları.
const ID_KEYS = {
  operation: ["operationID", "operationId"],
  offer: ["offerID", "offerId"],
  request: ["requestID", "requestId"],
  user: ["userID", "userId", "customerID", "customerId"],
  warehouse: ["warehouseID", "warehouseId"],
  "warehouse-room": ["roomID", "roomId", "warehouseRoomID", "warehouseID", "warehouseId"],
  company: ["companyID", "companyId", "warehouseCompanyID", "corporationID"],
  contract: ["contractID", "companyID", "userID"],
  "additional-service": ["additionalServiceID", "serviceID", "id"],
  mailing: ["receiverID", "id"],
};

function endpointFromUrl(url) {
  if (typeof url !== "string") return "";
  const i = url.indexOf("flow/");
  let ep = i >= 0 ? url.slice(i + 5) : url.slice(url.lastIndexOf("/") + 1);
  const q = ep.indexOf("?");
  if (q >= 0) ep = ep.slice(0, q);
  return ep.trim();
}

function pick(obj, keys) {
  if (!obj || typeof obj !== "object") return "";
  for (const k of keys) {
    const v = obj[k];
    if (v !== null && v !== undefined && v !== "" && typeof v !== "object") return String(v);
  }
  return "";
}

function parseBody(data) {
  if (!data) return null;
  if (typeof data === "string") {
    try { return JSON.parse(data); } catch (e) { return null; }
  }
  if (typeof data === "object") return data;
  return null;
}

function resolveId(entity, reqBody, result) {
  const keys = ID_KEYS[entity] || ["id"];
  let id = pick(result, keys);
  if (!id && Array.isArray(result)) id = pick(result[0], keys);
  if (!id) id = pick(reqBody, keys);
  // Bulunamazsa "liste düzeyinde değişiklik" sentinel'i — alıcı yine de tazeler.
  return id || "*";
}

// Aynı (entity,id) için kısa süreli istek patlamalarını tek yayında topla.
const timers = new Map();
function emitDebounced(entity, id) {
  const key = entity + ":" + id;
  if (timers.has(key)) clearTimeout(timers.get(key));
  timers.set(key, setTimeout(() => {
    timers.delete(key);
    emitStatusUpdate({ entity, id, source: "auto" });
  }, 250));
}

let installed = false;
export function installRealtimeSync() {
  if (installed) return;
  installed = true;
  axios.interceptors.response.use(
    (response) => {
      try {
        const cfg = response.config || {};
        if (String(cfg.method || "").toLowerCase() !== "post") return response;

        const ep = endpointFromUrl(cfg.url);
        if (!ep || SKIP_EXACT.has(ep) || SKIP_PREFIX.some((p) => ep.startsWith(p))) return response;

        const entity = entityOf(ep);
        if (!entity) return response;

        // Sadece backend "status" olumlu döndüyse yayınla (validasyon hatalarını atla).
        if (!response.data || !response.data.status) return response;

        const id = resolveId(entity, parseBody(cfg.data), response.data.result);
        emitDebounced(entity, id);
        // Kuplajlı entity'leri de liste düzeyinde tazele (ör. offer → operation).
        (RELATED[entity] || []).forEach((rel) => emitDebounced(rel, "*"));
      } catch (e) { /* sessizce geç */ }
      return response;
    },
    (error) => Promise.reject(error)
  );
}
